import { cva } from "class-variance-authority";
import type { ReactNode } from "react";

import { cn } from "../lib/cn";
import { BUSY_CONTROL, FOCUS_RING } from "../lib/focus";
import { Spinner } from "../Spinner/Spinner";

/**
 * The persistent, self-paced outcome message.
 *
 * This product has no toasts and no snackbars, for success or for error, on
 * any platform including Android. An outcome is announced by something the fan
 * reads at their own pace and dismisses themselves (`05-system-states.md` §8).
 * The offline shape is a warm inline banner directly below the header, never
 * covering content (`05-system-states.md` §4).
 *
 * This component never auto-dismisses. There is no timer and no duration
 * prop. The screen that mounted it removes it, or the fan presses dismiss.
 */

export type typeInlineBannerVariant =
  "offline" | "warning" | "success" | "neutral";

export type typeInlineBannerAction = {
  label: string;
  onAction: () => void;
  /**
   * The action is running. Named `loading` to match `Button`, which is this
   * kit's word for the same state: spinner, `aria-busy`, `data-loading`, and
   * presses that no longer do anything.
   *
   * The control stays FOCUSABLE while it runs, the way `Button` does. The
   * rule and the reason live in one place, `BUSY_CONTROL` in `../lib/focus`.
   * This file differs from `Button` only in which classes carry the
   * treatment, not in the rule.
   *
   * The label is deliberately unchanged while busy: the control keeps one
   * accessible name from start to finish.
   */
  loading?: boolean | undefined;
  /**
   * Announced while `loading`, the way `Button`'s `loadingLabel` is. Verb
   * first, like the label: "Reloading your pools."
   *
   * It is read out by the banner's own live region rather than added to the
   * button, so the control keeps one accessible name and a consumer gets the
   * announcement without building a live region of its own. Omit it on a
   * screen that already announces this in words of its own, so the fan is not
   * told the same thing twice.
   */
  loadingLabel?: string | undefined;
};

export type typeInlineBannerProps = {
  /** Defaults to "neutral". */
  variant?: typeInlineBannerVariant | undefined;
  /** One sentence the fan reads at their own pace. Sentence case. */
  message: string;
  /** A single follow-up. "Try again" on the offline banner. */
  action?: typeInlineBannerAction | undefined;
  /** Renders a dismiss control. Omit for a banner that must stay put. */
  onDismiss?: (() => void) | undefined;
  className?: string | undefined;
};

export const inlineBannerVariants = cva(
  "flex flex-col gap-2 rounded-md border p-4 text-body sm:flex-row sm:items-start sm:gap-3",
  {
    variants: {
      variant: {
        neutral: "bg-info-tint border-info text-ink",
        success: "bg-success-tint border-success text-ink",
        warning: "bg-warning-tint border-warning text-ink",
        offline: "bg-warning-tint border-warning text-ink",
      },
    },
    defaultVariants: { variant: "neutral" },
  },
);

const successIcon = (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 20 20"
    fill="none"
    className={cn("h-icon-md w-icon-md shrink-0", "text-success")}
  >
    <circle cx="10" cy="10" r="8.25" stroke="currentColor" strokeWidth="1.75" />
    <path
      d="m6.25 10.25 2.5 2.5 5-5.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const infoIcon = (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 20 20"
    fill="none"
    className={cn("h-icon-md w-icon-md shrink-0", "text-info")}
  >
    <circle cx="10" cy="10" r="8.25" stroke="currentColor" strokeWidth="1.75" />
    <path
      d="M10 9v5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="10" cy="6.25" r="1.1" fill="currentColor" />
  </svg>
);

const warningIcon = (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 20 20"
    fill="none"
    className={cn("h-icon-md w-icon-md shrink-0", "text-warning")}
  >
    <path
      d="M10 2.75 18.25 17.25H1.75Z"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinejoin="round"
    />
    <path
      d="M10 8v3.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="10" cy="14.25" r="1.1" fill="currentColor" />
  </svg>
);

const VARIANT_ICON: Record<typeInlineBannerVariant, ReactNode> = {
  success: successIcon,
  neutral: infoIcon,
  warning: warningIcon,
  offline: warningIcon,
};

export const InlineBanner = ({
  variant,
  message,
  action,
  onDismiss,
  className,
}: typeInlineBannerProps): ReactNode => {
  const resolvedVariant = variant ?? "neutral";
  const isActionLoading = action?.loading === true;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="inline-banner"
      data-variant={resolvedVariant}
      className={cn(inlineBannerVariants({ variant }), className)}
    >
      {VARIANT_ICON[resolvedVariant]}
      <p className="min-w-0 flex-1 text-ink">{message}</p>
      {action ? (
        <button
          type="button"
          onClick={() => {
            // `aria-disabled` is advice to assistive technology, not a block:
            // the press still arrives, so the early return is what actually
            // stops the action running twice.
            if (isActionLoading) return;
            action.onAction();
          }}
          aria-disabled={isActionLoading || undefined}
          aria-busy={isActionLoading || undefined}
          data-loading={isActionLoading ? "true" : undefined}
          data-testid="inline-banner-action"
          className={cn(
            "flex items-center gap-2 self-start rounded-sm text-caption text-primary underline underline-offset-4",
            "min-h-touch-target",
            "aria-disabled:cursor-not-allowed aria-disabled:no-underline",
            BUSY_CONTROL,
            FOCUS_RING,
          )}
        >
          {isActionLoading ? (
            <Spinner
              className="h-icon-sm w-icon-sm shrink-0"
              data-testid="inline-banner-spinner"
            />
          ) : null}
          {action.label}
        </button>
      ) : null}
      {isActionLoading && action.loadingLabel !== undefined ? (
        <span className="sr-only" data-testid="inline-banner-loading-label">
          {action.loadingLabel}
        </span>
      ) : null}
      {onDismiss ? (
        <button
          type="button"
          aria-label={`Dismiss ${message}`}
          onClick={onDismiss}
          className={cn(
            "-m-2 ml-auto flex h-touch-target w-touch-target shrink-0 items-center justify-center rounded-sm text-ink-secondary hover:text-ink",
            FOCUS_RING,
          )}
        >
          <svg
            aria-hidden="true"
            focusable="false"
            viewBox="0 0 16 16"
            fill="none"
            className="h-icon-sm w-icon-sm"
          >
            <path
              d="m4 4 8 8M12 4l-8 8"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
            />
          </svg>
        </button>
      ) : null}
    </div>
  );
};
